// The look the forge lays on a fight's board (docs/186 EA13, docs/166 E3/E4): rooms' own floors and a mine's rails,
// sent beside the board on `encounter_stage.look`. It never changes the board or the rules; only how the squares
// are drawn. The column is empty, or JSON `{"names":[...],"floors":"<rle>","rails":"<rle>"}`, each run-length
// string over the board's squares in row-major order (`value*count`, comma-separated, exactly w*h squares).

/** A decoded look: per square, 0 = the kit's own floor or `names[v - 1]`; and 1 where a rail runs. */
export interface BoardLook {
  /** The pieces `floors` names: `floor_flags`, `floor_planks`, …, `runner`. */
  names: string[];
  /** w*h, one value a square (absent: all 0). */
  floors?: Uint8Array;
  /** w*h, 1 where a rail runs (absent: none). */
  rails?: Uint8Array;
}

/** `0*40,1*12,0*3` → n values, or undefined when it is malformed or doesn't cover exactly n squares. */
export function decodeRuns(rle: string, n: number, max = 255): Uint8Array | undefined {
  const out = new Uint8Array(n);
  let at = 0;
  for (const part of rle.split(',')) {
    const m = /^\s*(\d+)\*(\d+)\s*$/.exec(part);
    if (!m) return undefined;
    const v = Number(m[1]), k = Number(m[2]);
    if (v > max || at + k > n) return undefined;
    if (v) out.fill(v, at, at + k);
    at += k;
  }
  return at === n ? out : undefined;
}

function warn(msg: string): void {
  if (import.meta.env?.DEV) console.warn(`encounter look: ${msg}; drawing the board without it`);
}

/**
 * The stage's `look` for a w×h board, or undefined: empty, malformed or the wrong length is drawn as no look
 * (warned in dev). A floor value past `names`, or a rail value other than 0 or 1, is malformed.
 */
export function decodeLook(raw: string | undefined, w: number, h: number): BoardLook | undefined {
  if (!raw || !raw.trim()) return undefined;
  let j: unknown;
  try { j = JSON.parse(raw); } catch { warn('not JSON'); return undefined; }
  if (!j || typeof j !== 'object') { warn('not an object'); return undefined; }
  const o = j as { names?: unknown; floors?: unknown; rails?: unknown };
  const names = Array.isArray(o.names) && o.names.every((s) => typeof s === 'string') ? (o.names as string[]) : o.names === undefined ? [] : undefined;
  if (!names) { warn('names is not a list of strings'); return undefined; }
  const look: BoardLook = { names };
  for (const key of ['floors', 'rails'] as const) {
    const v = o[key];
    if (v === undefined || v === null || v === '') continue;
    const runs = typeof v === 'string' ? decodeRuns(v, w * h, key === 'rails' ? 1 : names.length) : undefined;
    if (!runs) { warn(`${key} is malformed or not ${w}×${h}`); return undefined; }
    if (runs.some((x) => x)) look[key] = runs;
  }
  return look.floors || look.rails ? look : undefined;
}

/** A short key for a look (for a snapshot's hash): changes when any square changes. */
export function lookKey(raw: string | undefined): string {
  if (!raw) return '';
  let h = 2166136261;
  for (let i = 0; i < raw.length; i++) { h ^= raw.charCodeAt(i); h = Math.imul(h, 16777619); }
  return `${raw.length}:${(h >>> 0).toString(36)}`;
}
